import { flatMap, forEach, set } from 'lodash-es';
import { FormValues, ServiceGroupsInfo, ServiceInfo } from 'types';
import {
  parse,
  Duration,
  secondsToHours,
  secondsToMinutes,
  minutesInHour,
} from 'date-fns';
import { getBaseUrl } from 'api';

export const getServicesDuration = (
  serviceGroups: ServiceGroupsInfo,
  servicesValue: FormValues['favorIds'],
  serviceCounts: FormValues['favorCounts'],
): number => {
  if (servicesValue?.length) {
    const services = flatMap(serviceGroups).filter(({ id }) =>
      servicesValue.includes(id),
    );

    return services.reduce((prev, { duration, id }) => {
      const count = serviceCounts?.[id] ?? 1;
      return prev + duration * count;
    }, 0);
  }
  return 0;
};

// Длительность услуг и макс. перерыва после услуг
export const getServicesDurationWithMaxHoldAfter = (
  serviceGroups: ServiceGroupsInfo,
  servicesValue: FormValues['favorIds'],
  serviceCounts: FormValues['favorCounts'],
): number => {
  if (servicesValue?.length) {
    const services = flatMap(serviceGroups).filter(({ id }) =>
      servicesValue.includes(id),
    );

    const res = services.reduce(
      (prev, { duration, holdAfter, id }) => {
        const count = serviceCounts?.[id] ?? 1;
        return [prev[0] + duration * count, Math.max(prev[1], holdAfter ?? 0)];
      },
      [0, 0],
    );
    return res[0] + res[1];
  }
  return 0;
};

export const getMastersSkills = (
  serviceGroups: ServiceGroupsInfo,
): Record<string, string[]> => {
  const skills: Record<string, string[]> = {};

  forEach(serviceGroups, (services) =>
    services.forEach((service) =>
      service.employee?.forEach((masterId) => {
        skills[masterId] = skills[masterId]
          ? skills[masterId].concat(service.id)
          : [service.id];
      }),
    ),
  );

  return skills;
};

export const parseTimeSlots = (timeSlots: string[]): Date[] =>
  timeSlots.map((time) => parse(time, 'HH:mm', new Date()));

/**
 * almost copy-pasted iso8601-duration library
 */

const defaultDuration: Required<Duration> = Object.freeze({
  years: 0,
  months: 0,
  weeks: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
});

const objMap: (keyof Duration)[] = [
  'years',
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
];

const isoPattern =
  /P(?:(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(?:T(\d+(?:[.,]\d+)?H)?(\d+(?:[.,]\d+)?M)?(\d+(?:[.,]\d+)?S)?)?)/;

export const parseISO8601 = (isoDuration: string): Duration => {
  const matches = isoDuration.replace(/,/g, '.').match(isoPattern);
  if (!matches) {
    throw new RangeError(`invalid duration: ${isoDuration}`);
  }
  // Slice away first entry in match-array (the input string)
  const slicedMatches: (string | undefined)[] = matches.slice(1);
  if (slicedMatches.filter((v) => v != null).length === 0) {
    throw new RangeError(`invalid duration: ${isoDuration}`);
  }
  // Check only one fraction is used
  if (slicedMatches.filter((v) => /\./.test(v || '')).length > 1) {
    throw new RangeError(`only the smallest unit can be fractional`);
  }

  return slicedMatches.reduce(
    (prev, next, idx) => set(prev, objMap[idx], parseFloat(next || '0') || 0),
    {} as Duration,
  );
};

export const durationEnds = (
  durationInput: Duration,
  startDate = new Date(),
): Date => {
  const duration = { ...defaultDuration, ...durationInput };

  const timestamp = startDate.getTime();
  const then = new Date(timestamp);

  then.setFullYear(then.getFullYear() + duration.years);
  then.setMonth(then.getMonth() + duration.months);
  then.setDate(then.getDate() + duration.days);
  // set time as milliseconds to get fractions working for minutes/hours
  const hoursInMs = duration.hours * 3600 * 1000;
  const minutesInMs = duration.minutes * 60 * 1000;
  then.setMilliseconds(
    then.getMilliseconds() + duration.seconds * 1000 + hoursInMs + minutesInMs,
  );
  // Special case weeks
  then.setDate(then.getDate() + duration.weeks * 7);

  return then;
};

export const durationToSeconds = (
  durationInput: Duration,
  startDate = new Date(),
): number => {
  const duration = { ...defaultDuration, ...durationInput };

  const timestamp = startDate.getTime();
  const now = new Date(timestamp);
  const then = durationEnds(duration, now);

  return (then.getTime() - now.getTime()) / 1000;
};

export const convertISO8601ToSeconds = (isoDuration: string): number =>
  durationToSeconds(parseISO8601(isoDuration));

export const getDurationMessage = (duration: number): string => {
  const hours = secondsToHours(duration);
  const minutes = secondsToMinutes(duration) - hours * minutesInHour;

  return `${hours ? `${hours} ч ` : ''}${minutes ? `${minutes} мин` : ''}`;
};

export const getPriceText = (service: ServiceInfo, multi = 1): string => {
  const { price, priceMax, priceInterval, showPrice } = service;
  if (!showPrice) return '';
  const priceMin = price * multi;
  if (priceInterval) {
    return priceMax ? `${priceMin} - ${priceMax * multi}₽` : `от ${priceMin}₽`;
  }
  return `${priceMin}₽`;
};

export const getImageUrl = (
  widgetId: string,
  entityName: string,
  objectId: string,
): string =>
  `${getBaseUrl()}/booking-widget/client/attachment/widget/${widgetId}/${entityName}/object/${objectId}/inline`;

export const checkPattern = (
  value: string,
  pattern: string[],
): string | null => {
  if (value && pattern.includes(value)) {
    return value;
  }

  return null;
};
